import { useEffect, useMemo, useState } from 'react';
import tw from 'twin.macro';

import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { SearchBar } from '@insights_module/components/SearchBar';
import { Checkbox } from '@components/Checkbox';
import { useInfiniteMegaphoneCampaigns } from '@insights_module/hooks/integrations/useInfiniteMegaphoneCampaigns';
import { useInfiniteMegaphoneCampaignOrders } from '@insights_module/hooks/integrations/useInfiniteMegaphoneCampaignOrders';
import { MegaphoneCampaignOrder } from '@insights_module/types/Megaphone';
import { Loader } from '@components/Loader';
import { Info } from '@icons/Info';
import { ToolTip } from '@insights_module/components/ToolTip';
import { useDebouncedState } from '@app/hooks/useDebounce';
import { TriangleExclamationMarkIcon } from '@icons/TriangleExclamationMarkIcon';
import { Button } from '@components/Button';
import { CreateTrackingPixelParamsType } from '@account_module/types/Integration';
import { useUser } from '@auth_module/hooks/useUser';
import { createTrackingPixel } from '@services/index';
import { useNotification } from '@insights_module/hooks/useNotification';
import { Modal } from '@components/Modal';

const Container = tw.div`absolute top-[96px] right-[104px] flex items-start rounded border border-gray-1 bg-neutral-background shadow-modal backdrop-blur-[22px] w-[432px] z-[1000]`;
const Wrapper = tw.div`flex flex-col justify-center items-end gap-[16px] rounded p-[16px] w-full bg-neutral-background`;
const InnerWrapper = tw.div`flex flex-col justify-center items-end gap-[8px] rounded w-full bg-neutral-background p-[8px]`;
const SelectCampaignWrapper = tw.div`flex flex-col gap-[8px] self-stretch rounded-lg items-stretch`;
const SelectedCampaign = tw.div`flex items-start px-[8px] text-[16px] text-text-and-icon font-bold self-stretch`;
const SelectCampaignLabel = tw.p`text-[13px] text-text-and-icon font-semibold text-start`;
const SelectOrderLabel = tw.p`text-[14px] text-text-and-icon font-semibold text-start self-stretch`;
const ActionsWrapper = tw.div`flex justify-end items-end self-stretch gap-[24px]`;
const Divider = tw.div`w-full h-[1px] bg-gray-1`;
const SelectAllWrapper = tw.div`flex justify-between text-text-and-icon text-[14px] font-semibold w-full`;
const InfoWrapper = tw.div`flex opacity-70 text-[12px] font-medium gap-[4px]`;

const emptyOption = {
  value: null,
  label: <Option>Search for campaign name</Option>,
};

const CampaignOrderItem = ({
  order,
  isSelected,
  onSelected,
}: {
  order: MegaphoneCampaignOrder;
  isSelected: boolean;
  onSelected: (isSelected: boolean) => void;
}) => {
  const handleSelect = () => {
    if (order.sounderTrackingUrlSet || order.sounderTrackingUrlMax) return;
    onSelected(!isSelected);
  };

  const pixelInfo = useMemo(() => {
    if (!order.sounderTrackingUrlSet && !order.sounderTrackingUrlMax) return null;
    if (order.sounderTrackingUrlSet) return 'Tracked';
    return 'Pixel limit';
  }, [order]);

  return (
    <div
      tw='flex p-[8px] items-center gap-[8px] self-stretch rounded hover:bg-gray-2 w-full justify-between text-[14px] text-text-and-icon-muted font-medium'
      css={[
        (order.sounderTrackingUrlSet || order.sounderTrackingUrlMax) &&
          tw`opacity-50 cursor-auto hover:bg-none`,
      ]}>
      <Checkbox
        tw='flex truncate justify-start gap-[8px] w-[70%]'
        checked={isSelected}
        onChange={handleSelect}>
        <div
          tw='truncate'
          css={[(order.sounderTrackingUrlSet || order.sounderTrackingUrlMax) && tw`cursor-auto`]}
          title={order.title}>
          {order.title}
        </div>
      </Checkbox>
      <div>{pixelInfo}</div>
    </div>
  );
};

const InfoItem = ({ title, info, tooltip }: { title: string; info: number; tooltip: string }) => (
  <div tw='flex gap-[4px] items-center border-b border-gray-1'>
    <span>{info}</span>
    {title}
    <ToolTip content={<span tw='text-text-and-icon'>{tooltip}</span>} placement='top'>
      <Info tw='stroke-text-and-icon-muted w-[12px] h-[12px]' />
    </ToolTip>
  </div>
);

const ErrorBanner = () => (
  <div tw='flex items-center justify-center self-stretch p-[16px] gap-[16px] bg-error-bg rounded-lg border border-error-icon-bg'>
    <div tw='flex min-w-[34px] min-h-[34px] items-center rounded-full bg-error-icon-bg justify-center'>
      <TriangleExclamationMarkIcon tw='fill-error-text-icon w-[26px] h-[27px] mt-[-6px]' />
    </div>
    <div tw='text-error-text-icon text-[14px] font-medium'>
      Error occur, you need to set permission for adding pixel tracker. If you not sure,{' '}
      <a href='mailto:support@sounder.ai' tw='font-bold underline text-error-text-icon'>
        contact our support.
      </a>
    </div>
  </div>
);

export const AddTrackingPixelModal = () => {
  const { notify, NTFC_TYPE } = useNotification();
  const { organization } = useUser();
  const { showCreateTrackingPixelModal, setShowCreateTrackingPixelModal } =
    useIntegrationsContext();
  const [selectedCampaign, setSelectedCampaign] = useState<IDropdownOption>(emptyOption);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [selectedOrdersIds, setSelectedOrdersIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedValue = useDebouncedState(searchTerm, 300);
  const [isCreatingTrackingPixel, setIsCreatingTrackingPixel] = useState(false);
  const { campaigns, error, isLoading, keyword, setKeyword } = useInfiniteMegaphoneCampaigns();
  const {
    campaignOrders,
    totalMegaphoneCampaignOrders,
    isInitialLoading,
    availableOrdersCount,
    trackedOrdersCount,
    limitedOrdersCount,
  } = useInfiniteMegaphoneCampaignOrders(selectedCampaign.value, debouncedValue);

  const campaignOptions: IDropdownOption[] = useMemo(() => {
    return campaigns.map((campaign) => ({
      value: campaign.id,
      label: <Option>{campaign.title}</Option>,
      meta: { title: campaign.title },
    }));
  }, [campaigns]);

  const handleCancel = () => {
    setSelectedCampaign(emptyOption);
    setShowCreateTrackingPixelModal(false);
    setKeyword('');
  };

  const handleAddTrackingPixel = () => {
    if (!organization || !selectedCampaign.value || !selectedOrdersIds.length) return;

    setIsCreatingTrackingPixel(true);
    const payload: CreateTrackingPixelParamsType = {
      data: selectedOrdersIds.map((orderId) => ({
        provider: 'megaphone',
        organization_id: organization.id,
        campaign_id: selectedCampaign.value as string,
        extra_params: {
          campaign_order_id: orderId,
        },
      })),
    };

    createTrackingPixel(payload)
      .then(() => {
        notify({ type: NTFC_TYPE.SUCCESS, message: 'Tracking pixel/s created successfully.' });
      })
      .catch(() => {
        notify({
          type: NTFC_TYPE.DANGER,
          message: 'Something went wrong, please try again later.',
        });
      })
      .finally(() => {
        setSelectedCampaign(emptyOption);
        setSelectedOrdersIds([]);
        setIsCreatingTrackingPixel(false);
        setShowCreateTrackingPixelModal(false);
      });
  };

  useEffect(() => {
    if (!campaignOrders.length) return;

    if (isSelectAllChecked) {
      const newSelectedOrdersIds = campaignOrders.reduce((acc, order) => {
        if (order.sounderTrackingUrlSet || order.sounderTrackingUrlMax) return acc;
        return [...acc, order.id];
      }, [] as string[]);
      setSelectedOrdersIds(newSelectedOrdersIds);
    } else {
      setSelectedOrdersIds([]);
    }
  }, [isSelectAllChecked, campaignOrders]);

  useEffect(() => {
    setSelectedOrdersIds([]);
  }, [selectedCampaign.value]);

  if (!showCreateTrackingPixelModal) return null;

  return (
    <Modal onClose={() => handleCancel()}>
      <Container>
        <Wrapper>
          <InnerWrapper>
            {!!error && <ErrorBanner />}
            {!selectedCampaign.value && !error && (
              <SelectCampaignWrapper>
                <SelectCampaignLabel>Select Campaign</SelectCampaignLabel>
                <Dropdown
                  onChange={(val) => setSelectedCampaign(val as IDropdownOption)}
                  value={selectedCampaign}
                  options={campaignOptions}
                  dropdownMenuStyle={tw`w-full`}
                  isSearchable={true}
                  searchPlaceholder='Search for campaign name'
                  onSearch={setKeyword}
                  initialSearchTerm={keyword}
                  isLoading={isLoading}
                  showNoResults={true}
                />
              </SelectCampaignWrapper>
            )}
            {!!selectedCampaign.value && (
              <>
                <SelectedCampaign>{selectedCampaign.meta?.title}</SelectedCampaign>
                <Divider />
                <div tw='flex flex-col gap-[8px] self-stretch'>
                  <SelectOrderLabel>Select Campaign Order for Selected Campaign</SelectOrderLabel>
                  <SearchBar
                    tw='h-[30px] bg-neutral-background text-text-and-icon'
                    placeholder='Search for order name'
                    value={searchTerm}
                    onChange={(val) => setSearchTerm(val as string)}
                  />
                  <div tw='flex flex-col gap-[2px] max-h-[280px] overflow-scroll'>
                    <div tw='flex p-[8px] items-center gap-[8px] self-stretch rounded bg-gray-2 text-text-and-icon'>
                      <Checkbox
                        tw='flex justify-start gap-[8px]'
                        checked={isSelectAllChecked}
                        onChange={() => setIsSelectAllChecked(!isSelectAllChecked)}></Checkbox>
                      <SelectAllWrapper>
                        {`${isSelectAllChecked ? 'Deselect All' : 'Select All'}`}
                        {!isInitialLoading &&
                          `(${availableOrdersCount}/${totalMegaphoneCampaignOrders})`}
                        <InfoWrapper>
                          {!!trackedOrdersCount && (
                            <InfoItem
                              title='Tracked'
                              info={trackedOrdersCount}
                              tooltip='An order already have tracking URL.'
                            />
                          )}
                          {!!trackedOrdersCount && !!limitedOrdersCount && (
                            <div tw='w-[1px] h-[20px] bg-gray-2' />
                          )}
                          {!!limitedOrdersCount && (
                            <InfoItem
                              title='Pixel Limit'
                              info={limitedOrdersCount}
                              tooltip='An order already has 5 tracking URLs so we could not add ours.'
                            />
                          )}
                        </InfoWrapper>
                      </SelectAllWrapper>
                    </div>
                    {isInitialLoading ? (
                      <Loader size='md' theme='dark' />
                    ) : (
                      !!campaignOrders.length &&
                      campaignOrders.map((order) => (
                        <CampaignOrderItem
                          order={order}
                          key={order.id}
                          isSelected={selectedOrdersIds.includes(order.id)}
                          onSelected={(isSelected) => {
                            if (isSelected) {
                              setSelectedOrdersIds([...selectedOrdersIds, order.id]);
                            } else {
                              setSelectedOrdersIds(
                                selectedOrdersIds.filter((id) => id !== order.id)
                              );
                            }
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </InnerWrapper>
          <ActionsWrapper>
            <Button.Secondary
              tw='bg-neutral-background border-gray-1 text-text-and-icon h-[25px] w-[57px] text-[11px]'
              onClick={handleCancel}>
              Cancel
            </Button.Secondary>
            <Button.Primary
              tw='bg-accent-2 text-text-and-icon-invert h-[25px] w-[72px]'
              css={[!selectedOrdersIds.length && tw`opacity-50 cursor-auto`]}
              disabled={!selectedOrdersIds.length || isCreatingTrackingPixel}
              isLoading={isCreatingTrackingPixel}
              onClick={handleAddTrackingPixel}>
              Create
            </Button.Primary>
          </ActionsWrapper>
        </Wrapper>
      </Container>
    </Modal>
  );
};
